import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Typography from "./components/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Internal
import AppAppBar from "./views/AppAppBar";
import AppFooter from "./views/AppFooter";
import withRoot from "../../withRoot";

function Submissions() {
  const [expandedAccordion, setExpandedAccordion] = React.useState<
    string | false
  >(false);

  const handleAccordionHover = (accordion: string) => {
    setExpandedAccordion(accordion);
  };

  const handleAccordionLeave = () => {
    setExpandedAccordion(false);
  };

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppAppBar />

      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: "url(/images/submissions-hero.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "white",
          minHeight: { xs: "60vh", md: "70vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 500,
            letterSpacing: "1px",
            mb: 2,
            fontSize: { xs: "2.5rem", sm: "3.75rem" },
          }}
        >
          SUBMISSIONS
        </Typography>
      </Box>

      {/* About Applications Section */}
      <Box
        sx={{
          maxWidth: { xs: "100vw", md: "50vw" },
          textAlign: "center",
          m: "0 auto",
          padding: { xs: "10px 20px", md: "20px" },
          mt: { xs: 4, md: 8 },
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", fontSize: { xs: "1.2rem", md: "1.5rem" } }}
        >
          ABOUT APPLICATIONS
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          InkBowl Publishing welcomes literature, visual art, and multimedia
          from high school, undergraduate and graduate school students in the
          United States, UK, EU or Canada. Both stand-alone projects and
          collections are accepted. The application portal is conveniently
          located on students&apos; OneCreator dashboard, where applicants can
          also track their submission review status. The latency period may vary
          based on the submission volume at the time or the lengthiness of your
          application. Individuals can specify their preference to be a
          contributor to InkBowl&apos;s digital editions, in-print biannual
          magazine issues, or both.
        </Typography>
      </Box>

      {/* Published Work Section */}
      <Box
        sx={{
          maxWidth: { xs: "100vw", md: "50vw" },
          textAlign: "center",
          m: "0 auto",
          padding: { xs: "10px 20px", md: "20px" },
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", fontSize: { xs: "1.2rem", md: "1.5rem" } }}
        >
          PUBLISHED WORK
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          Once confirmed, those who elected to be published virtually will have
          their work immediately showcased on the InkBowl website. Students
          should expect to wait longer to be notified weather or not they will
          be featured in an upcoming magazine, as those positions are highly
          competitive and contenders require additional assessment. Formalized
          publications will be displayed on authors or artists&apos; InkLink
          profile, accompanied by a verification badge. Authors and artists will
          be publicly credited but may use a pseudonym if desired.
        </Typography>
      </Box>

      {/* Qualifications Section */}
      <Box
        sx={{
          maxWidth: { xs: "100vw", md: "50vw" },
          textAlign: "center",
          m: "0 auto",
          padding: { xs: "10px 20px", md: "20px" },
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", fontSize: { xs: "1.2rem", md: "1.5rem" } }}
        >
          QUALIFICATIONS
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          InkBowl strives to publish authentic creative work held at a high
          caliber. We consider over 50 types of literature and artistic pieces
          to accommodate diverse specialties. All applications are reviewed
          diligently by our editorial board to ensure they meet our requirements
          and standards. Submissions are subject to rejection for quality or
          content purposes.
        </Typography>
      </Box>

      {/* Criteria for Applicants Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: { xs: "auto", md: "60vh" },
          mt: { xs: 2, md: 4 },
          mx: { xs: 0, md: 2 },
          gap: { xs: 0, md: 2 },
        }}
      >
        {/* Image Half */}
        <Box
          sx={{
            backgroundImage: "url(/images/submissions-1.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            minHeight: { xs: "40vh", md: "60vh" },
            flex: { xs: "none", md: "1" },
            width: { xs: "100%", md: "50%" },
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              px: { xs: 2, md: 4 },
              display: { xs: "block", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "white",
                fontWeight: "bold",
                fontSize: { xs: "1.8rem", md: "2.5rem" },
              }}
            >
              CRITERIA FOR APPLICANTS
            </Typography>
          </Box>
        </Box>

        {/* Text Half */}
        <Box
          sx={{
            flex: { xs: "none", md: "1" },
            width: { xs: "100%", md: "50%" },
            display: "flex",
            alignItems: "center",
            py: { xs: 4, md: 6 },
            px: { xs: 3, md: 6 },
            backgroundColor: "white",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                p: 0,
              }}
            >
              <Typography
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  lineHeight: 1.8,
                  color: "black",
                }}
              >
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Persons who are formally enrolled students
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Pursuants of a high school, undergraduate or graduate degree
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Citizens of the United States, UK, EU or Canada
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Current residents of the United States, UK, EU or Canada
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Attend a school within the United States, UK, EU or Canada
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Individuals 16 years of age or older
                  </Typography>
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Spacer between sections */}
      <Box sx={{ height: { xs: "2rem", md: "3rem" } }} />

      {/* Criteria for Published Work Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: { xs: "auto", md: "60vh" },
          mt: { xs: 2, md: 4 },
          mx: { xs: 0, md: 2 },
          gap: { xs: 0, md: 2 },
        }}
      >
        {/* Image Half */}
        <Box
          sx={{
            backgroundImage: "url(/images/submissions-2.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            minHeight: { xs: "40vh", md: "60vh" },
            flex: { xs: "none", md: "1" },
            width: { xs: "100%", md: "50%" },
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              px: { xs: 2, md: 4 },
              display: { xs: "block", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "white",
                fontWeight: "bold",
                fontSize: { xs: "1.8rem", md: "2.5rem" },
              }}
            >
              CRITERIA FOR PUBLISHED WORK
            </Typography>
          </Box>
        </Box>

        {/* Text Half */}
        <Box
          sx={{
            flex: { xs: "none", md: "1" },
            width: { xs: "100%", md: "50%" },
            display: "flex",
            alignItems: "center",
            py: { xs: 4, md: 6 },
            px: { xs: 3, md: 6 },
            backgroundColor: "white",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                p: 0,
              }}
            >
              <Typography
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  lineHeight: 1.8,
                  color: "black",
                }}
              >
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    All content is original
                  </Typography>
                </Box>
                <Box component="li" sx={{ mb: 1, ml: 3 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: { xs: "0.85rem", md: "0.9rem" } }}
                  >
                    ○ Students will be held accountable for plagiarism
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Submissions are strictly non-academic
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Applications exclude media or references from outside
                    sources
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Authors use the English language
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Writing is proofread to eliminate all grammatical errors
                  </Typography>
                </Box>
                <Box component="li" sx={{ mb: 1, ml: 3 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: { xs: "0.85rem", md: "0.9rem" } }}
                  >
                    ○ Unless they are an intentional aspect of the narrative
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Visual art is uploaded as a clear, high-resolution image
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Pieces demonstrate aesthetic and stylistic consideration
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "flex-start" }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontWeight: "bold" }}
                  >
                    •
                  </Typography>
                  <Typography component="span">
                    Creators and their work adhere to InkBowl&apos;s Community
                    Guidelines and Terms and Conditions
                  </Typography>
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Spacer between sections */}
      <Box sx={{ height: { xs: "2rem", md: "3rem" } }} />

      {/* Accepted Work Formats Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: { xs: "auto", md: "60vh" },
          mt: { xs: 1, md: 2 },
          mx: { xs: 0, md: 2 },
          gap: { xs: 0, md: 2 },
        }}
      >
        {/* Left Half - Title and Text */}
        <Box
          sx={{
            flex: { xs: "none", md: "1" },
            width: { xs: "100%", md: "50%" },
            display: "flex",
            alignItems: "center",
            py: { xs: 4, md: 6 },
            px: { xs: 3, md: 6 },
            backgroundColor: "white",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "1.2rem", md: "1.5rem" },
                mb: 3,
              }}
            >
              ACCEPTED WORK FORMATS
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1rem", md: "1.2rem" },
                lineHeight: "2",
              }}
            >
              Below is a list of work formats that are currently officially
              accepted for publication. Our platform is lenient if your
              submission does not fall directly under one of these categories as
              long as it complies with our Criteria for Published Work outlined
              above.
            </Typography>
          </Box>
        </Box>

        {/* Right Half - Dropdowns */}
        <Box
          sx={{
            flex: { xs: "none", md: "1" },
            width: { xs: "100%", md: "50%" },
            display: "flex",
            alignItems: "center",
            py: { xs: 4, md: 6 },
            px: { xs: 3, md: 6 },
            backgroundColor: "white",
          }}
        >
          <Box sx={{ width: "100%" }}>
            {/* Literature Accordion */}
            <Accordion
              expanded={expandedAccordion === "literature"}
              onMouseEnter={() => handleAccordionHover("literature")}
              onMouseLeave={handleAccordionLeave}
              sx={{
                mb: 2,
                boxShadow: "none",
                "&:before": { display: "none" },
                backgroundColor: "white",
                "& .MuiAccordionSummary-root": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: {
                      xs: "transparent",
                      md: "rgba(0,0,0,0.04)",
                    },
                  },
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "black" }} />}
                sx={{
                  minHeight: "auto",
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  padding: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "black",
                    fontSize: { xs: "1rem", md: "1.1rem" },
                  }}
                >
                  LITERATURE
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "0 0 16px 16px" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    lineHeight: 1.6,
                    color: "#666",
                  }}
                >
                  • Poetry and verse collections
                  <br />
                  • Short stories and novellas
                  <br />
                  • Creative nonfiction essays
                  <br />
                  • Flash fiction and micro-stories
                  <br />
                  • Literary criticism and reviews
                  <br />• Experimental and avant-garde writing
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Visual Art Accordion */}
            <Accordion
              expanded={expandedAccordion === "visual"}
              onMouseEnter={() => handleAccordionHover("visual")}
              onMouseLeave={handleAccordionLeave}
              sx={{
                mb: 2,
                boxShadow: "none",
                "&:before": { display: "none" },
                backgroundColor: "white",
                "& .MuiAccordionSummary-root": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: {
                      xs: "transparent",
                      md: "rgba(0,0,0,0.04)",
                    },
                  },
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "black" }} />}
                sx={{
                  minHeight: "auto",
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  padding: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "black",
                    fontSize: { xs: "1rem", md: "1.1rem" },
                  }}
                >
                  VISUAL ART
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "0 0 16px 16px" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    lineHeight: 1.6,
                    color: "#666",
                  }}
                >
                  • Digital artwork and illustrations
                  <br />
                  • Photography and photojournalism
                  <br />
                  • Paintings and drawings
                  <br />
                  • Graphic design and typography
                  <br />
                  • Sculpture and 3D art
                  <br />• Mixed media installations
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Mixed Media Accordion */}
            <Accordion
              expanded={expandedAccordion === "mixed"}
              onMouseEnter={() => handleAccordionHover("mixed")}
              onMouseLeave={handleAccordionLeave}
              sx={{
                mb: 2,
                boxShadow: "none",
                "&:before": { display: "none" },
                backgroundColor: "white",
                "& .MuiAccordionSummary-root": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: {
                      xs: "transparent",
                      md: "rgba(0,0,0,0.04)",
                    },
                  },
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "black" }} />}
                sx={{
                  minHeight: "auto",
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                  },
                  padding: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "black",
                    fontSize: { xs: "1rem", md: "1.1rem" },
                  }}
                >
                  MIXED MEDIA
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "0 0 16px 16px" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    lineHeight: 1.6,
                    color: "#666",
                  }}
                >
                  • Interactive digital storytelling
                  <br />
                  • Video essays and documentaries
                  <br />
                  • Audio narratives and podcasts
                  <br />
                  • Web-based art projects
                  <br />
                  • Performance art documentation
                  <br />• Collaborative multimedia works
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>

      <AppFooter />
    </Box>
  );
}

export default withRoot(Submissions);
